import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { api } from 'common';
import { C2, C2_FONT, C2TableCard } from './Table/C2UserTable';
import { settingsFieldSx } from './settings/C2SettingsKit';

const emptyRow = () => ({
  min_fare: '',
  rate_per_unit_distance: '',
  rate_per_hour: '',
  base_fare: '',
  source: '',
});

/**
 * Admin: referencias de competencia (Uber/DiDi aprox) por ciudad y vehículo.
 * Se guardan en fare_market_benchmarks y las usa el asesor DeepSeek.
 */
export default function FareMarketBenchmarksEditor() {
  const dispatch = useDispatch();
  const {
    fetchFareMarketBenchmarks,
    saveFareMarketBenchmark,
    deleteFareMarketBenchmark,
    fetchZonificacionCities,
  } = api;
  const benchmarks = useSelector((s) => s.fareMarketAdvisor?.benchmarks || {});
  const cities = useSelector((s) => s.zonificacion?.cities || []);
  const cartypes = useSelector((s) => s.cartypes?.cars || []);
  const canEdit = !!useSelector((s) => s.settingsdata?.settings?.AllowCriticalEditsAdmin);

  const activeCities = useMemo(
    () => (cities || []).filter((c) => c.active !== false).sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [cities]
  );
  const activeCars = useMemo(
    () =>
      (cartypes || [])
        .filter((c) => c.visibleInApp !== false)
        .sort((a, b) => (a.pos || 0) - (b.pos || 0)),
    [cartypes]
  );

  const [cityId, setCityId] = useState('');
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    dispatch(fetchFareMarketBenchmarks());
    if (fetchZonificacionCities) dispatch(fetchZonificacionCities());
  }, [dispatch, fetchFareMarketBenchmarks, fetchZonificacionCities]);

  useEffect(() => {
    if (!cityId && activeCities.length) setCityId(activeCities[0].id);
  }, [activeCities, cityId]);

  const selectedCity = useMemo(
    () => activeCities.find((c) => c.id === cityId) || null,
    [activeCities, cityId]
  );

  const cityBench = useMemo(() => {
    if (!selectedCity) return {};
    return (
      benchmarks[selectedCity.id] ||
      benchmarks[selectedCity.name] ||
      {}
    );
  }, [benchmarks, selectedCity]);

  useEffect(() => {
    if (!selectedCity) return;
    const next = {};
    activeCars.forEach((car) => {
      const saved = cityBench[car.name] || cityBench[car.id] || {};
      next[car.name] = {
        min_fare: saved.min_fare != null ? String(saved.min_fare) : '',
        rate_per_unit_distance:
          saved.rate_per_unit_distance != null ? String(saved.rate_per_unit_distance) : '',
        rate_per_hour: saved.rate_per_hour != null ? String(saved.rate_per_hour) : '',
        base_fare: saved.base_fare != null ? String(saved.base_fare) : '',
        source: saved.source || '',
        checkedAt: saved.checkedAt || null,
      };
    });
    setDraft(next);
  }, [selectedCity, cityBench, activeCars]);

  const setRow = (carName, field, value) => {
    setDraft((d) => ({
      ...d,
      [carName]: { ...(d[carName] || emptyRow()), [field]: value },
    }));
  };

  const onSaveRow = async (car) => {
    if (!canEdit || !selectedCity) return;
    const row = draft[car.name] || emptyRow();
    setBusy(`save-${car.id}`);
    setNotice(null);
    const result = await saveFareMarketBenchmark({
      cityKey: selectedCity.id,
      cityName: selectedCity.name,
      carKey: car.name,
      carTypeId: car.id,
      min_fare: row.min_fare,
      rate_per_unit_distance: row.rate_per_unit_distance,
      rate_per_hour: row.rate_per_hour,
      base_fare: row.base_fare,
      source: row.source,
    });
    setBusy(null);
    if (result.success) {
      setNotice({ severity: 'success', message: `Guardado: ${selectedCity.name} · ${car.name}` });
    } else {
      setNotice({ severity: 'error', message: result.error || 'No se pudo guardar.' });
    }
  };

  const onDeleteRow = async (car) => {
    if (!canEdit || !selectedCity) return;
    setBusy(`del-${car.id}`);
    setNotice(null);
    const result = await deleteFareMarketBenchmark({
      cityKey: selectedCity.id,
      carKey: car.name,
    });
    // También limpia alias por nombre de ciudad si existiera
    if (selectedCity.name) {
      await deleteFareMarketBenchmark({
        cityKey: selectedCity.name,
        carKey: car.name,
      }).catch(() => null);
    }
    setBusy(null);
    if (result.success) {
      setNotice({ severity: 'success', message: `Eliminado: ${car.name}` });
    } else {
      setNotice({ severity: 'error', message: result.error || 'No se pudo eliminar.' });
    }
  };

  const onSaveAll = async () => {
    if (!canEdit || !selectedCity) return;
    setBusy('all');
    setNotice(null);
    try {
      for (const car of activeCars) {
        const row = draft[car.name] || emptyRow();
        const hasAny =
          String(row.min_fare || '').trim() ||
          String(row.rate_per_unit_distance || '').trim() ||
          String(row.rate_per_hour || '').trim() ||
          String(row.base_fare || '').trim() ||
          String(row.source || '').trim();
        if (!hasAny) continue;
        // eslint-disable-next-line no-await-in-loop
        await saveFareMarketBenchmark({
          cityKey: selectedCity.id,
          cityName: selectedCity.name,
          carKey: car.name,
          carTypeId: car.id,
          min_fare: row.min_fare,
          rate_per_unit_distance: row.rate_per_unit_distance,
          rate_per_hour: row.rate_per_hour,
          base_fare: row.base_fare,
          source: row.source,
        });
      }
      setNotice({
        severity: 'success',
        message: `Referencias de ${selectedCity.name} guardadas. Usa “Solo analizar” en Tarifas mercado.`,
      });
    } catch (e) {
      setNotice({ severity: 'error', message: e?.message || 'Error al guardar.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <C2TableCard sx={{ mb: 2, p: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <CompareArrowsOutlinedIcon sx={{ color: C2.violet, fontSize: 36 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar }}>
            Referencias de competencia (por ciudad)
          </Typography>
          <Typography sx={{ fontFamily: C2_FONT, color: C2.muted, fontSize: '0.88rem', mt: 0.35 }}>
            Anota tarifas aproximadas de Uber, DiDi u otras (mínima, $/km y $/minuto T). El asesor DeepSeek las usa al analizar. No es tiempo real: tú las actualizas.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Ciudad"
          value={cityId}
          onChange={(e) => setCityId(e.target.value)}
          sx={{ ...settingsFieldSx, minWidth: 200 }}
          InputLabelProps={{ style: { fontFamily: C2_FONT } }}
        >
          {activeCities.map((c) => (
            <MenuItem key={c.id} value={c.id} sx={{ fontFamily: C2_FONT }}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          disabled={!canEdit || !!busy || !selectedCity}
          onClick={onSaveAll}
          sx={{ fontFamily: C2_FONT, textTransform: 'none', bgcolor: C2.violet }}
        >
          {busy === 'all' ? 'Guardando…' : 'Guardar ciudad'}
        </Button>
      </Stack>

      {!canEdit ? (
        <Alert severity="info" sx={{ mb: 1.5, fontFamily: C2_FONT }}>
          Modo demostración: puedes ver las referencias, pero no se guardan.
        </Alert>
      ) : null}
      {notice ? (
        <Alert
          severity={notice.severity}
          onClose={() => setNotice(null)}
          sx={{ mb: 1.5, fontFamily: C2_FONT }}
        >
          {notice.message}
        </Alert>
      ) : null}

      {!selectedCity ? (
        <Typography sx={{ fontFamily: C2_FONT, color: C2.muted }}>
          No hay ciudades activas en Zonificación.
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {activeCars.map((car) => {
            const row = draft[car.name] || emptyRow();
            return (
              <Grid item xs={12} key={car.id}>
                <Box
                  sx={{
                    border: `1px solid ${C2.border}`,
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: '#FAFAFC',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ md: 'flex-start' }}
                  >
                    <Box sx={{ minWidth: 140, pt: 1 }}>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar }}>
                        {car.name}
                      </Typography>
                      {row.checkedAt ? (
                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>
                          Act. {new Date(row.checkedAt).toLocaleString('es-CO')}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>
                          Sin referencia
                        </Typography>
                      )}
                    </Box>
                    <TextField
                      size="small"
                      label="Mínima (COP)"
                      value={row.min_fare}
                      onChange={(e) => setRow(car.name, 'min_fare', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ ...settingsFieldSx, width: { xs: '100%', md: 130 } }}
                    />
                    <TextField
                      size="small"
                      label="$ / km"
                      value={row.rate_per_unit_distance}
                      onChange={(e) => setRow(car.name, 'rate_per_unit_distance', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ ...settingsFieldSx, width: { xs: '100%', md: 110 } }}
                    />
                    <TextField
                      size="small"
                      label="$ / min (T)"
                      value={row.rate_per_hour}
                      onChange={(e) => setRow(car.name, 'rate_per_hour', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ inputMode: 'decimal' }}
                      helperText="Tiempo en minutos"
                      FormHelperTextProps={{ sx: { fontFamily: C2_FONT, mx: 0.25, mt: 0 } }}
                      sx={{ ...settingsFieldSx, width: { xs: '100%', md: 120 } }}
                    />
                    <TextField
                      size="small"
                      label="Base (opc.)"
                      value={row.base_fare}
                      onChange={(e) => setRow(car.name, 'base_fare', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ ...settingsFieldSx, width: { xs: '100%', md: 110 } }}
                    />
                    <TextField
                      size="small"
                      label="Fuente (UberX, DiDi…)"
                      value={row.source}
                      onChange={(e) => setRow(car.name, 'source', e.target.value)}
                      disabled={!canEdit}
                      placeholder="UberX Barranquilla abril 2026"
                      sx={{ ...settingsFieldSx, flex: 1, minWidth: 180 }}
                    />
                    <Stack direction="row" spacing={0.5} sx={{ pt: 0.25 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canEdit || !!busy}
                        onClick={() => onSaveRow(car)}
                        sx={{ fontFamily: C2_FONT, textTransform: 'none' }}
                      >
                        {busy === `save-${car.id}` ? '…' : 'Guardar'}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={!canEdit || !!busy}
                        onClick={() => onDeleteRow(car)}
                        startIcon={<DeleteOutlineIcon />}
                        sx={{ fontFamily: C2_FONT, textTransform: 'none' }}
                      >
                        Quitar
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </C2TableCard>
  );
}
